import type { HTMLAttributes } from "react";

import { cn } from "@/lib/utils";

/**
 * A small round identity color swatch. Used wherever a person/entity carries an
 * assigned color — participant dots on cards, the author dot on a sticky note,
 * the color-picker swatches. Presentational; wrap it in a <button> when it
 * needs to be clickable.
 */
export interface ColorDotProps extends Omit<HTMLAttributes<HTMLSpanElement>, "color"> {
  color: string;
  size?: "xs" | "sm" | "md";
  /** Draw a ring to mark the active/selected swatch. */
  selected?: boolean;
  /** Hairline border so light dots read against the background (used in stacks). */
  bordered?: boolean;
}

const SIZES: Record<NonNullable<ColorDotProps["size"]>, string> = {
  xs: "h-2 w-2",
  sm: "h-3 w-3",
  md: "h-4 w-4",
};

export function ColorDot({
  color,
  size = "sm",
  selected = false,
  bordered = false,
  className,
  ...rest
}: ColorDotProps) {
  return (
    <span
      {...rest}
      className={cn(
        "inline-block shrink-0 rounded-full",
        SIZES[size],
        bordered && "border border-background",
        selected && "ring-2 ring-foreground/40 ring-offset-1 ring-offset-background",
        className,
      )}
      style={{ backgroundColor: color, ...rest.style }}
    />
  );
}

export interface ColorDotStackProps {
  items: { color: string; title?: string }[];
  /** Cap the visible dots; the rest collapse into a "+N" chip. */
  max?: number;
  size?: "xs" | "sm" | "md";
  className?: string;
}

/** Overlapping row of ColorDots — e.g. the participants on a session card. */
export function ColorDotStack({ items, max = 6, size = "sm", className }: ColorDotStackProps) {
  const shown = items.slice(0, max);
  const extra = items.length - shown.length;
  return (
    <span className={cn("inline-flex items-center", className)}>
      <span className="flex -space-x-1">
        {shown.map((it, i) => (
          <ColorDot key={i} color={it.color} size={size} bordered title={it.title} />
        ))}
      </span>
      {extra > 0 ? (
        <span className="ml-1 text-[10px] font-medium text-muted-foreground">+{extra}</span>
      ) : null}
    </span>
  );
}
